import React, { forwardRef, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';


import styled from '@emotion/styled';
import { FormikContextType } from 'formik';

import StringCounter from './StringCounter';
import { DeleteIcon } from '../../Icons';

interface Props {
  formik: FormikContextType<any>;
  name: string;
  label?: string;
  labelStyle?: CSSProperties;
  value?: string;
  slot?: JSX.Element;
  limitCount?: number;
  stringCounterStyle?: CSSProperties;
  isResetBtn?: boolean;
  isEnglishOnly?: boolean;
  isProfileInput?: boolean;
  isUsernameOrPassword?: boolean;
  isModalType?: boolean;
  validationSentence?: string;
  enterSubmit?: () => void;
  onChange?: (e?: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  type?: string;
  className?: string;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  style?: CSSProperties;
  inputStyle?: CSSProperties;
  usernameErrors?: any; // profile page 한정
  disabled?: boolean;
  disabledStyle?: CSSProperties;
}

function UserRegistrationNumberInputForm(props: Props, ref?: React.Ref<HTMLInputElement>) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const checkEngAndNumOnly = (e: any) => (e.target.value = e.target.value.replace(/[^A-Za-z0-9+]/, '')); // 영어와 숫자만 입력할 수 있게

  const handleOnKor = (e: any) => {
    if (props?.limitCount && e.target.value.length > props.limitCount) {
      e.target.value = e.target.value.slice(0, props.limitCount); // 1 length당 2 bytes인 char일 경우 마지막 글자가 입력되는 것 방지
    }
  };

  const handleKeyDownEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (props?.onKeyDown) {
      props.onKeyDown(e);
    }
    if (props?.enterSubmit && e.key === 'Enter') {
      props.enterSubmit();
    }
  };

  const handleFocus = () => {
    // focusOut 될 때만 touched 객체가 셋팅되기 때문에, focusOn 되었을 때 fieldTouched true로 설정해줌
    props.formik.setFieldTouched(props.name, true);
    props.onFocus?.();
  };

  const isError = !!(props.formik.touched[props.name] && props.formik.errors[props.name]);
  const isShort = props.formik.errors[props.name] === 'Too short';

  const isResetBtn = props.isResetBtn ?? true;

  return (
    <SWrapper
      $isError={isError}
      $isShort={isShort}
      $usernameErrors={props?.usernameErrors}
      className={props?.className}
      style={props?.style}
    >
      {/* {props.label && <label style={props.labelStyle}>{props.label}</label>} */}
      <SInputWrapper $isError={isError}>
        <input
          className="ellipsis"
          type={props?.type ?? 'text'}
          name={props.name}
          value={props.value ?? props.formik.values[props.name] ?? ''}
          onChange={props?.onChange ?? props.formik.handleChange}
          placeholder={props?.placeholder}
          maxLength={props?.isUsernameOrPassword ? 25 : props?.limitCount}
          ref={ref || inputRef}
          onInput={(e) => {
            props?.isEnglishOnly ? checkEngAndNumOnly(e) : handleOnKor(e);
          }}
          onFocus={handleFocus}
          autoComplete={props?.autoComplete}
          autoFocus={props?.autoFocus}
          onKeyDown={handleKeyDownEnter}
          style={{ ...props.inputStyle, ...(props.disabled && props.disabledStyle) }}
          disabled={props.disabled}
        />
        {isResetBtn && props?.limitCount && (
          <SRightSideWithCounter>
            <>
              {props.formik.values[props.name] && (
                <button
                  className={props.isModalType ? 'reset-btn in-modal' : 'reset-btn'}
                  onClick={() => {
                    props.formik.setFieldValue(props.name, '');
                    inputRef.current?.focus();
                  }}
                >
                  <DeleteIcon />
                </button>
              )}
              <StringCounter
                currentCount={props.formik.values[props.name]?.length}
                limitCount={props.limitCount}
                style={{ ...props.stringCounterStyle }}
              />
            </>
          </SRightSideWithCounter>
        )}
        {isResetBtn && props.slot && (
          <SRightSideWithSlot>
            {props.formik.values[props.name] && (
              <button
                className={props.isModalType ? 'reset-btn in-modal' : 'reset-btn'}
                onClick={() => {
                  props.formik.setFieldValue(props.name, '');
                  inputRef.current?.focus();
                }}
              >
                <DeleteIcon />
              </button>
            )}
            {props.slot}
          </SRightSideWithSlot>
        )}
        {isResetBtn && !props.slot && !props.limitCount && (
          <SRightSideWithOnlyReset>
            {props.formik.values[props.name] && (
              <button
                className={props.isModalType ? 'reset-btn in-modal' : 'reset-btn'}
                onClick={() => {
                  props.formik.setFieldValue(props.name, '');
                  inputRef.current?.focus();
                }}
              >
                <DeleteIcon />
              </button>
            )}
          </SRightSideWithOnlyReset>
        )}
      </SInputWrapper>
      {props.validationSentence && (
        <div className="validation-sentence">
          {t(props.validationSentence)
            ?.split('\n')
            .map((text) =>
              isError ? (
                <p key={text} className="error">
                  {text}
                </p>
              ) : (
                <p key={text} className="default">
                  {text}
                </p>
              )
            )}
        </div>
      )}
    </SWrapper>
  );
}

export default forwardRef(UserRegistrationNumberInputForm);

const SWrapper = styled.div<{ $isError: boolean; $isShort: boolean; $usernameErrors: any }>`
  width: 500px;
  position: relative;

  label {
    font-size: 13px;
    color: ${({ theme }) => theme.color.text.TXT_2};
  }

  input {
    width: 100%;
    position: relative;
    padding: 13px 12px;
    font-size: 14px;
    border: 1px solid none;
    border-radius: 8px;
    background-color: ${({ theme }) => theme.palette.variant[15]};
    outline: none;
    transition: all 0.3s;
    ${({ theme }) => theme.font.body2_m};
    color: ${({ theme }) => theme.color.text.TXT_3};
    border: 1px solid ${({ $isError, theme }) => ($isError ? theme.color.static.warning : 'none')};
  }

  input:focus {
    border: 1px solid ${({ theme, $isError }) => ($isError ? theme.color.static.warning : theme.color.static.primary)};
  }

  input::placeholder {
    color: ${({ theme }) => theme.color.text.TXT_0};
  }

  input[type='password']::-ms-reveal {
    display: none;
  }

  input[type='password']::-webkit-contacts-auto-fill-button,
  input[type='password']::-webkit-credentials-auto-fill-button {
    position: absolute;
    right: 86px;
    bottom: 12px;
  }

  .validation-sentence {
    height: 17px;
    margin-top: 6px;
    width: 100%;
    font-size: 12px;
    list-style: inside;
    ${({ theme }) => theme.font.caption1_m};

    .error {
      padding: 0 12px;
      color: ${({ theme }) => theme.color.static.warning};
    }
    .default {
      padding: 0 12px;
      color: ${({ theme }) => theme.color.text.TXT_1};
    }
    li {
      width: 350px;
    }

    .li-0 {
      // alphanumeric validation msg
      color: ${({ theme, $usernameErrors: usernameErrors }) =>
        usernameErrors?.validationError ? theme.color.static.warning : theme.color.text.TXT_0};
    }

    .li-1 {
      // length validation msg
      color: ${({ theme, $usernameErrors: usernameErrors }) =>
        usernameErrors?.lengthError ? theme.color.static.warning : theme.color.text.TXT_0};
    }
  }
`;

type SInputWrapperProps = {
  $isError: boolean;
};
const SInputWrapper = styled.div<SInputWrapperProps>`
  position: relative;

  input {
    ${({ theme, $isError }) => ($isError ? theme.color.static.warning : 'none')};
  }
`;

const SRightSideWithCounter = styled.div`
  position: absolute;
  bottom: 14px;
  right: 10px;
  height: 20px;
  display: flex;
  column-gap: 4px;

  .reset-btn {
    width: 20px;
    height: 20px;
  }

  .in-modal {
    width: 20px;
    height: 20px;
    right: 37px;
    bottom: 0px;
  }

  img {
    width: 100%;
    height: 100%;
  }
`;

const SRightSideWithSlot = styled.div`
  position: absolute;
  bottom: 8px;
  right: 0px;

  .reset-btn {
    position: absolute;
    width: 20px;
    height: 20px;
    right: 53px;
  }

  img {
    width: 100%;
    height: 100%;
  }
`;

const SRightSideWithOnlyReset = styled.div`
  .reset-btn {
    position: absolute;
    width: 20px;
    height: 20px;
    right: 0;
    bottom: 8px;
  }

  img {
    width: 100%;
    height: 100%;
  }
`;
